<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import signInImage from '@/images/account/sign-in.jpg';
import { AuthRouteName } from '@/js/account/app/router/route-names';
import AuthTitle from '@/js/account/components/auth/AuthTitle.vue';
import BaseButton from '@/js/account/components/base/BaseButton.vue';
import BaseField from '@/js/account/components/base/BaseField.vue';
import { accountResetPassword } from '@/js/account/entities/auth';
import AuthLayout from '@/js/account/layouts/auth-layout/AuthLayout.vue';
import { useFormErrors } from '@/js/account/shared/lib';
import { classNames } from '@/js/lib/bem';

const route = useRoute();
const router = useRouter();
const {
  clearErrors,
  clearFieldOnInput,
  setFromAxiosError,
  setGeneralError,
  fieldError,
  hasFieldError,
  generalError,
} = useFormErrors();

const cn = classNames('reset-password-page');

const token = ref(typeof route.query.token === 'string' ? route.query.token : '');
const email = ref(typeof route.query.email === 'string' ? route.query.email : '');

// Dropped from the address bar as soon as they are read: GTM auto-collects
// document.location.href into page_location on every hit, which would send the raw reset secret
// to GA4. Costs reload-survivability of the link — the user has to reopen it from the email.
if (token.value !== '' || email.value !== '') {
  const params = new URLSearchParams(window.location.search);

  params.delete('token');
  params.delete('email');

  const query = params.toString();

  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${query === '' ? '' : `?${query}`}${window.location.hash}`,
  );
}

const password = ref('');
const passwordConfirmation = ref('');
const submitting = ref(false);

const resetLinkInvalid = computed(
  () => token.value === '' || email.value === '',
);

const linkError = computed(() =>
  resetLinkInvalid.value
    ? 'This password reset link is invalid. Request a new one.'
    : undefined,
);

async function onSubmit(): Promise<void> {
  if (resetLinkInvalid.value) {
    return;
  }

  clearErrors();

  if (password.value !== passwordConfirmation.value) {
    setGeneralError('Passwords do not match.');
    return;
  }

  submitting.value = true;

  try {
    const { status } = await accountResetPassword({
      token: token.value,
      email: email.value,
      password: password.value,
    });

    if (status === 'passwords.reset') {
      await router.push({ name: AuthRouteName.PasswordChangedSuccess });
      return;
    }

    setGeneralError('Could not reset your password. Please try again.');
  }
  catch (error) {
    setFromAxiosError(error);
  }
  finally {
    submitting.value = false;
  }
}
</script>

<template>
  <AuthLayout :src="signInImage" alt="">
    <div :class="cn()">
      <AuthTitle :class="cn('title')" title="Reset password">
        <template #description>
          <p :class="cn('description')">
            Set your new password
          </p>
        </template>
      </AuthTitle>

      <p v-if="linkError" :class="cn('link-error')" role="alert">
        {{ linkError }}
      </p>

      <form :class="cn('form')" @submit.prevent="onSubmit">
        <BaseField
          v-model="password"
          :class="cn('field')"
          label="New Password"
          name="password"
          type="password"
          autocomplete="new-password"
          :invalid="hasFieldError('password') || !!generalError || !!linkError"
          :message="
            fieldError('password')
              || linkError
              || (generalError ? generalError : undefined)
          "
          :disabled="resetLinkInvalid"
          required
          @update:model-value="
            clearFieldOnInput('password', { clearGeneral: true })
          "
        />
        <BaseField
          v-model="passwordConfirmation"
          :class="cn('field')"
          label="Confirm New Password"
          name="password_confirmation"
          type="password"
          autocomplete="new-password"
          :invalid="hasFieldError('password_confirmation')"
          :message="fieldError('password_confirmation')"
          :disabled="resetLinkInvalid"
          required
          @update:model-value="
            clearFieldOnInput('password_confirmation', { clearGeneral: true })
          "
        />

        <BaseButton
          :class="cn('button')"
          type="submit"
          full-width
          :loading="submitting"
          :disabled="resetLinkInvalid"
        >
          Reset password
        </BaseButton>
      </form>
    </div>
  </AuthLayout>
</template>

<style lang="scss" scoped>
.reset-password-page {
  max-width: 363px;
  width: 100%;

  &__form {
    display: flex;
    flex-direction: column;
  }

  &__field {
    margin-bottom: 24px;
  }

  &__button {
    margin-top: 8px;
    margin-bottom: 40px;
  }

  &__title {
    text-align: center;
  }

  &__description {
    margin: 0;
    text-align: center;
    font-family: var(--font-family-sans);
    font-weight: var(--font-weight-regular);
    font-size: 16px;
    line-height: 1.125;
    color: var(--color-text-secondary);
  }

  &__link-error {
    margin: 0 0 24px;
    text-align: center;
    font-family: var(--font-family-sans);
    font-size: 14px;
    line-height: 1.375;
    color: var(--base-field-error-color, #db1515);
  }
}
</style>
